<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

const WIDGET_TOKEN = window.chatwootConfig?.supportWidgetToken || '';
const HOST = window.chatwootConfig?.hostURL || '';
const STORAGE_KEY = 'cw-support-widget-auth';

const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const isSending = ref(false);
const authToken = ref(null);
const pubsubToken = ref(null);
const messagesContainer = ref(null);
let cableSubscription = null;
let pollingTimer = null;

const currentUser = computed(() => store.getters.getCurrentUser || {});

// ─── Persistence ───
function loadAuth() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (saved?.token) {
      authToken.value = saved.token;
      pubsubToken.value = saved.pubsubToken;
      return true;
    }
  } catch { /* noop */ }
  return false;
}

function saveAuth(token, psToken) {
  authToken.value = token;
  pubsubToken.value = psToken;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, pubsubToken: psToken }));
}

function apiHeaders() {
  const h = { 'Content-Type': 'application/json' };
  if (authToken.value) h['X-Auth-Token'] = authToken.value;
  return h;
}

// ─── Bootstrap ───
async function bootstrap() {
  isLoading.value = true;
  try {
    const res = await fetch(
      `${HOST}/api/v1/widget/config?website_token=${WIDGET_TOKEN}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
    );
    if (!res.ok) throw new Error(`Config ${res.status}`);

    const data = await res.json();
    const token = data.website_channel_config?.auth_token;
    const psToken = data.contact?.pubsub_token;
    if (!token) throw new Error('No auth_token');

    saveAuth(token, psToken);
    await identifyContact();
    await fetchMessages();
    connectRealTime();
  } catch (e) {
    console.error('[SupportWidget] Bootstrap failed:', e);
  } finally {
    isLoading.value = false;
  }
}

async function identifyContact() {
  if (!authToken.value || !currentUser.value.id) return;
  try {
    await fetch(`${HOST}/api/v1/widget/contact?website_token=${WIDGET_TOKEN}`, {
      method: 'PATCH',
      headers: apiHeaders(),
      body: JSON.stringify({
        name: currentUser.value.name || 'Agente',
        email: currentUser.value.email || '',
        identifier: `agent_${currentUser.value.id}`,
      }),
    });
  } catch { /* noop */ }
}

// ─── Messages ───
async function fetchMessages() {
  if (!authToken.value) return;
  try {
    const res = await fetch(`${HOST}/api/v1/widget/messages?website_token=${WIDGET_TOKEN}`, {
      headers: apiHeaders(),
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 404) {
        localStorage.removeItem(STORAGE_KEY);
        authToken.value = null;
        await bootstrap();
        return;
      }
      return;
    }
    const data = await res.json();
    const raw = data.payload || data || [];
    messages.value = raw
      .filter(m => m.content && m.content_type !== 'input_csat')
      .sort((a, b) => a.id - b.id);
    scrollToBottom();
  } catch (e) {
    console.error('[SupportWidget] Fetch error:', e);
  }
}

async function sendMessage() {
  const content = newMessage.value.trim();
  if (!content || isSending.value || !authToken.value) return;

  isSending.value = true;
  newMessage.value = '';

  try {
    const convRes = await fetch(
      `${HOST}/api/v1/widget/conversations?website_token=${WIDGET_TOKEN}`,
      { headers: apiHeaders() }
    );
    let hasConversation = false;
    if (convRes.ok) {
      const convData = await convRes.json();
      hasConversation = !!(convData?.id || convData?.display_id);
    }

    if (!hasConversation) {
      await fetch(`${HOST}/api/v1/widget/conversations?website_token=${WIDGET_TOKEN}`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({
          contact: { name: currentUser.value.name, email: currentUser.value.email },
          message: { content, timestamp: new Date().toISOString() },
        }),
      });
      await fetchMessages();
    } else {
      const res = await fetch(`${HOST}/api/v1/widget/messages?website_token=${WIDGET_TOKEN}`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({ message: { content } }),
      });
      if (res.ok) {
        const msg = await res.json();
        if (msg?.id && !messages.value.find(m => m.id === msg.id)) {
          messages.value.push(msg);
        }
        scrollToBottom();
      }
    }
  } catch (e) {
    console.error('[SupportWidget] Send error:', e);
    newMessage.value = content;
  } finally {
    isSending.value = false;
  }
}

// ─── Real-time ───
function connectRealTime() {
  if (!pubsubToken.value) { startPolling(); return; }
  try {
    const wsUrl = HOST.replace(/^http/, 'ws') + '/cable';
    const cable = window.ActionCable?.createConsumer?.(wsUrl);
    if (!cable) { startPolling(); return; }

    cableSubscription = cable.subscriptions.create(
      { channel: 'RoomChannel', pubsub_token: pubsubToken.value },
      {
        received(raw) {
          const event = raw?.message?.event || raw?.event;
          const msg = raw?.message?.data || raw?.data;
          if (event === 'message.created' && msg?.content) {
            if (!messages.value.find(m => m.id === msg.id)) {
              messages.value.push(msg);
              scrollToBottom();
            }
          }
        },
      }
    );
  } catch { startPolling(); }
}

function startPolling() {
  if (pollingTimer) return;
  pollingTimer = setInterval(fetchMessages, 8000);
}

// ─── UI ───
function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function isFromMe(msg) {
  return msg.message_type === 0 || msg.message_type === 'incoming';
}

function senderLabel(msg) {
  if (isFromMe(msg)) return 'Você';
  return msg.sender?.name || 'Suporte';
}

// ─── Lifecycle ───
onMounted(async () => {
  if (!WIDGET_TOKEN) return;
  if (loadAuth()) {
    await fetchMessages();
    connectRealTime();
  } else {
    await bootstrap();
  }
});

onUnmounted(() => {
  if (pollingTimer) clearInterval(pollingTimer);
  cableSubscription?.unsubscribe?.();
});
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Messages area -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2.5" style="scroll-behavior: smooth;">
      <!-- Empty state -->
      <div v-if="messages.length === 0 && !isLoading" class="flex flex-col items-center justify-center h-full gap-2.5 text-center px-4">
        <div class="size-14 rounded-2xl bg-green-500/10 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"
            stroke="#10b981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <div class="text-sm font-semibold text-n-slate-12">Olá! 👋</div>
        <div class="text-xs text-n-slate-10 leading-relaxed">
          Precisa de ajuda? Envie uma mensagem<br>e nosso time responderá em breve.
        </div>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center h-full">
        <div class="flex items-center gap-2 text-xs text-n-slate-9">
          <span class="size-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          Carregando...
        </div>
      </div>

      <!-- Bubbles -->
      <template v-for="msg in messages" :key="msg.id">
        <div v-if="msg.content" class="flex" :class="isFromMe(msg) ? 'justify-end' : 'justify-start'">
          <div class="max-w-[85%] flex flex-col" :class="isFromMe(msg) ? 'items-end' : 'items-start'">
            <div class="px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap"
              :class="isFromMe(msg)
                ? 'bg-green-500 text-white rounded-2xl rounded-br-lg'
                : 'bg-n-alpha-2 text-n-slate-12 rounded-2xl rounded-bl-lg'">
              {{ msg.content }}
            </div>
            <span class="text-[10px] text-n-slate-9 mt-0.5 px-1">
              {{ senderLabel(msg) }} · {{ formatTime(msg.created_at) }}
            </span>
          </div>
        </div>
      </template>
    </div>

    <!-- Input area -->
    <div class="px-3 py-2.5 border-t border-n-weak shrink-0">
      <div class="flex items-center gap-2">
        <input
          v-model="newMessage"
          class="flex-1 px-3.5 py-2 rounded-xl border border-n-weak bg-transparent text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all"
          placeholder="Digite sua mensagem..."
          @keydown.enter.prevent="sendMessage"
        />
        <button
          class="size-9 rounded-xl flex items-center justify-center border-none cursor-pointer transition-all shrink-0 disabled:opacity-30"
          style="background: linear-gradient(135deg, #10b981 0%, #059669 100%);"
          :disabled="!newMessage.trim() || isSending"
          @click="sendMessage">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
